import React from 'react';
import { Link, useLocation } from 'react-router';
import SEO from '../components/SEO';
import GlowCard from '../components/GlowCard';
import AIImage from '../components/AIImage';
import { trackEvent } from '../components/analytics';
import FAQSection from '../components/FAQSection';
import { SITE_URL } from '../constants';
import { FAQItem } from '../types';

type RoleSection = {
  title: string;
  description: string;
  items?: string[];
};

type RoleLink = {
  label: string;
  path: string;
};

type LmsRolePageProps = {
  title: string;
  seoTitle: string;
  description: string;
  eyebrow: string;
  heroNote: string;
  heroImagePrompt: string;
  heroImageAlt: string;
  painPoints: string[];
  capabilities: string[];
  workflow: RoleSection[];
  outcomes: string[];
  relatedLinks: RoleLink[];
  ctaId: string;
  keywords?: string[];
  faq?: FAQItem[];
};

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const LmsRolePage: React.FC<LmsRolePageProps> = ({
  title,
  seoTitle,
  description,
  eyebrow,
  heroNote,
  heroImagePrompt,
  heroImageAlt,
  painPoints,
  capabilities,
  workflow,
  outcomes,
  relatedLinks,
  ctaId,
  keywords = [],
  faq = [],
}) => {
  const { pathname } = useLocation();
  const pageUrl = `${SITE_URL}${pathname}`;
  const workflowSectionId = 'how-it-works';
  const anchors = [
    { label: 'Pain Points', href: '#pain-points' },
    { label: 'Capabilities', href: '#capabilities' },
    { label: 'Workflow', href: `#${workflowSectionId}` },
    { label: 'Outcomes', href: '#outcomes' },
    ...(faq.length > 0 ? [{ label: 'FAQ', href: '#faq' }] : []),
  ];
  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'LMS',
          item: `${SITE_URL}/modules/lms`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: seoTitle,
          item: pageUrl,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: `${seoTitle} deployment workflow`,
      description,
      step: workflow.map((section, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        name: section.title,
        text: [section.description, ...(section.items || [])].join(' '),
      })),
    },
    ...(faq.length > 0
      ? [{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faq.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }]
      : []),
  ];

  return (
    <div className="animate-fade-in-up">
      <SEO
        title={seoTitle}
        description={description}
        keywords={keywords}
        faq={faq}
        schema={schema}
      />

      <section className="py-24 bg-slate-950 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <nav className="flex items-center space-x-2 text-slate-500 text-sm mb-6 uppercase tracking-widest">
              <span>Modules</span>
              <span>/</span>
              <span>LMS</span>
              <span>/</span>
              <span className="text-accent">{eyebrow}</span>
            </nav>
            <h1 className="text-5xl lg:text-7xl font-bold font-heading mb-8 leading-tight">
              {title}
            </h1>
            <p className="text-xl text-slate-400 mb-6 leading-relaxed">
              {description}
            </p>
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-10">
              {heroNote}
            </p>
            <nav aria-label="Page sections" className="flex flex-wrap gap-3 mb-10">
              {anchors.map((anchor) => (
                <a
                  key={anchor.href}
                  href={anchor.href}
                  className="px-4 py-2 text-xs uppercase tracking-widest text-slate-300 border border-white/10 rounded-full hover:border-accent/40 hover:text-accent transition"
                >
                  {anchor.label}
                </a>
              ))}
            </nav>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact?product=lms"
                className="inline-flex justify-center px-10 py-4 bg-accent text-primary font-bold rounded-xl"
                onClick={() => trackEvent('demo_request', { form_id: ctaId, module: 'lms' })}
              >
                Request LMS Walkthrough
              </Link>
              <Link
                to="/modules/lms"
                className="inline-flex justify-center px-10 py-4 border border-white/10 text-white font-bold rounded-xl hover:bg-white/5"
              >
                View Core LMS
              </Link>
            </div>
          </div>
          <div className="relative p-1 bg-slate-800 rounded-2xl overflow-hidden border border-white/10">
            <AIImage
              prompt={heroImagePrompt}
              alt={heroImageAlt}
              className="w-full opacity-95"
              aspectRatio="16:9"
              loading="eager"
            />
          </div>
        </div>
      </section>

      <section id="pain-points" className="py-24 px-4 border-b border-white/5">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          <GlowCard className="p-10">
            <h2 className="text-3xl font-bold font-heading mb-8 text-white">Operational pressure points</h2>
            <ul className="space-y-3">
              {painPoints.map((item) => (
                <li key={item} className="text-slate-400 text-sm flex items-start gap-3">
                  <span className="text-accent mt-1">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </GlowCard>

          <GlowCard id="capabilities" className="p-10">
            <h2 className="text-3xl font-bold font-heading mb-8 text-white">What AviaGov LMS adds</h2>
            <ul className="space-y-3">
              {capabilities.map((item) => (
                <li key={item} className="text-slate-300 text-sm flex items-start gap-3">
                  <span className="text-accent mt-1">✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </GlowCard>
        </div>
      </section>

      <section id={workflowSectionId} className="py-24 px-4 bg-slate-950/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">How this deployment works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {workflow.map((section) => (
              <GlowCard key={section.title} className="p-10" id={slugify(section.title)}>
                <h3 className="text-2xl font-bold text-white mb-4">{section.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">{section.description}</p>
                {section.items && (
                  <ul className="space-y-3">
                    {section.items.map((item) => (
                      <li key={item} className="text-slate-500 text-sm flex items-start gap-3">
                        <span className="text-accent mt-1">✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </GlowCard>
            ))}
          </div>
        </div>
      </section>

      <section id="outcomes" className="py-24 px-4 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">Expected outcomes</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {outcomes.map((item) => (
              <GlowCard key={item} className="p-8">
                <p className="text-slate-300 text-base leading-relaxed">{item}</p>
              </GlowCard>
            ))}
          </div>
        </div>
      </section>

      {faq.length > 0 && (
        <section id="faq">
          <FAQSection
            items={faq}
            title={`${seoTitle} FAQ`}
            subtitle="Answers to common questions about airline, authority, and regulated aviation LMS deployments."
          />
        </section>
      )}

      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl font-bold font-heading mb-6 text-white">Explore role-specific LMS deployments</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            {relatedLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="px-8 py-4 border border-white/10 text-white font-bold rounded-xl hover:bg-white/5"
              >
                {link.label}
              </Link>
            ))}
          </div>
          <Link
            to="/contact?product=lms"
            className="inline-flex px-10 py-4 bg-accent text-primary font-bold rounded-xl"
            onClick={() => trackEvent('demo_request', { form_id: `${ctaId}_footer`, module: 'lms' })}
          >
            Discuss Deployment Scope
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LmsRolePage;
